import React from "react";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Calendar as CalendarIcon, 
  Clock, 
  Loader2, 
  Edit, 
  Trash2 
} from "lucide-react";
import { getVehicleIcon } from "./routeConstants";

/**
 * Route Card Component
 * 
 * Displays a single shared route with actions.
 */
export function RouteCard({ route, onEdit, onCancel, cancelling }) {
  const VehicleIcon = getVehicleIcon(route.vehicle);
  
  return (
    <div className="border border-border p-6" data-testid={`route-card-${route.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
            <VehicleIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 font-bold text-lg">
              <span>{route.origin}</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              <span>{route.destination}</span>
            </div>
            {route.via?.length > 0 && (
              <p className="text-sm text-muted-foreground">
                via {route.via.join(" → ")}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <RouteStatusBadge status={route.status} />
          {route.status === "active" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(route)}
                className="rounded-none"
                data-testid={`edit-route-${route.id}`}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onCancel(route.id)}
                disabled={cancelling === route.id}
                className="rounded-none"
                data-testid={`cancel-route-${route.id}`}
              >
                {cancelling === route.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 pt-4 border-t border-border">
        <div>
          <p className="label-text mb-1">Date</p>
          <div className="flex items-center gap-1">
            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{route.departure_date}</span>
          </div>
        </div>
        <div>
          <p className="label-text mb-1">Time</p>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{route.departure_time}</span>
          </div>
        </div>
        <div>
          <p className="label-text mb-1">Available</p>
          <p className="font-medium">{route.available_space} / {route.total_space} kg</p>
        </div>
        <div>
          <p className="label-text mb-1">Price</p>
          <p className="font-medium">INR {route.price_per_kg}/kg</p>
        </div>
      </div>
    </div>
  );
}

function RouteStatusBadge({ status }) {
  const statusStyles = {
    active: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
    completed: "bg-blue-100 text-blue-800",
    default: "bg-gray-100 text-gray-800"
  };
  
  return (
    <span className={`px-3 py-1 text-xs font-bold uppercase ${statusStyles[status] || statusStyles.default}`}>
      {status}
    </span>
  );
}
